/**
 * Marketplace-aware API helpers for Fulfillment panels and TSD (WB vs Ozon).
 * Ozon list endpoints use 1-based page; WB / TablePagination — 0-based.
 * Warehouse calls go through `/fulfillment/unified` (legacy WB/Ozon paths kept).
 */

/** Normalize query/UI marketplace to `wb` | `ozon` | `yandex` | ''. */
export function normalizeFulfillmentMp(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'ozon' || raw === '2') return 'ozon';
  if (raw === 'wb' || raw === '1' || raw === 'wildberries') return 'wb';
  if (raw === 'yandex' || raw === '3' || raw === 'ym') return 'yandex';
  return '';
}

/**
 * Единая warehouse-база (picking / labeling / pallets / loading).
 * Маркетплейс определяется на бэкенде по account_id (и опционально marketplace).
 */
export function fulfillmentApiBase(_marketplace) {
  return '/fulfillment/unified';
}

export function isLocalWarehouseMp(marketplace) {
  const mp = normalizeFulfillmentMp(marketplace);
  return mp === 'ozon' || mp === 'yandex';
}

export function fulfillmentListPage(marketplace, zeroBasedPage) {
  const page = Math.max(0, Number(zeroBasedPage) || 0);
  return isLocalWarehouseMp(marketplace) ? page + 1 : page;
}

/** Для unified warehouse facade account_id (+ marketplace) нужны и WB, и Ozon. */
export function withAccountBody(marketplace, accountId, body = {}) {
  const next = { ...body };
  if (accountId != null && accountId !== '') next.account_id = accountId;
  const mp = normalizeFulfillmentMp(marketplace);
  if (mp) next.marketplace = mp;
  return next;
}

export function withAccountParams(marketplace, accountId, params = {}) {
  const next = { ...params };
  if (accountId != null && accountId !== '') next.account_id = accountId;
  const mp = normalizeFulfillmentMp(marketplace);
  if (mp) next.marketplace = mp;
  return next;
}

/** Marketplace of a unified/WB/Ozon account row. */
export function accountMarketplace(account) {
  const fromField = normalizeFulfillmentMp(account?.marketplace);
  if (fromField) return fromField;
  // Legacy WB rows without marketplace
  return 'wb';
}

/**
 * Pick cabinet id from unified accounts list using deep-link query.
 * Priority: exact account_id → first cabinet of mp → first account.
 */
export function pickFulfillmentAccount(accounts, query = {}) {
  const list = Array.isArray(accounts) ? accounts : [];
  if (!list.length) return '';

  const wantedId = query.account_id ?? query.accountId;
  if (wantedId != null && String(wantedId).trim() !== '') {
    const byId = list.find((item) => String(item.id) === String(wantedId));
    if (byId) return String(byId.id);
  }

  const mp = normalizeFulfillmentMp(query.mp ?? query.marketplace);
  if (mp) {
    const byMp = list.find((item) => {
      const itemMp = normalizeFulfillmentMp(item.marketplace);
      if (itemMp === mp) return true;
      // Legacy WB rows without marketplace field
      return mp === 'wb' && !itemMp;
    });
    if (byMp) return String(byMp.id);
  }

  return String(list[0].id);
}

/** Build `/fulfillment?...` path for redirects and in-app links. */
export function fulfillmentDeepLinkPath({ mp, accountId } = {}) {
  const params = new URLSearchParams();
  const norm = normalizeFulfillmentMp(mp);
  if (norm) params.set('mp', norm);
  if (accountId != null && String(accountId).trim() !== '') {
    params.set('account_id', String(accountId));
  }
  const q = params.toString();
  return q ? `/fulfillment?${q}` : '/fulfillment';
}

/**
 * Deep-link for TSD pages: `/fulfillment/labeling?mp=ozon&account_id=7`.
 * `basePath` without query (e.g. `/fulfillment/picking` or `/fulfillment/picking/12`).
 */
export function fulfillmentTsdPath(basePath, { mp, accountId } = {}) {
  const path = String(basePath || '/fulfillment').split('?')[0] || '/fulfillment';
  const params = new URLSearchParams();
  const norm = normalizeFulfillmentMp(mp);
  if (norm) params.set('mp', norm);
  if (accountId != null && String(accountId).trim() !== '') {
    params.set('account_id', String(accountId));
  }
  const q = params.toString();
  return q ? `${path}?${q}` : path;
}

/** Present Ozon posting as a task-like object for shared TSD UI. */
export function ozonPostingAsTask(posting = {}) {
  const number = posting.posting_number != null ? String(posting.posting_number) : null;
  return {
    ...posting,
    marketplace: posting.marketplace || 'ozon',
    account_id: posting.account_id,
    posting_number: number,
    wb_order_id: number,
    article: posting.offer_id || posting.article || posting.product?.article || null,
    nm_id: posting.sku ?? posting.nm_id ?? null,
    barcode: posting.barcode || posting.product?.barcode || null,
    image_url: posting.image_url || posting.product?.image_url || null,
    warehouse_id: posting.warehouse_id ?? null,
    delivery_method_id: posting.delivery_method_id ?? null,
    cargo_type: posting.cargo_type ?? null,
  };
}

/** Present Yandex order as a task-like object for shared TSD UI. */
export function yandexOrderAsTask(order = {}) {
  const number = order.yandex_order_id != null
    ? String(order.yandex_order_id)
    : (order.posting_number != null ? String(order.posting_number) : null);
  return {
    ...order,
    marketplace: order.marketplace || 'yandex',
    account_id: order.account_id,
    yandex_order_id: number,
    posting_number: number,
    wb_order_id: number,
    article: order.offer_id || order.article || order.product?.article || null,
    nm_id: order.sku ?? order.nm_id ?? null,
    barcode: order.barcode || order.product?.barcode || null,
    image_url: order.image_url || order.product?.image_url || null,
    warehouse_id: order.warehouse_id ?? null,
    cargo_type: order.cargo_type ?? null,
  };
}

function marketplaceOrderAsTask(row, marketplace) {
  return normalizeFulfillmentMp(marketplace) === 'yandex'
    ? yandexOrderAsTask(row)
    : ozonPostingAsTask(row);
}

/* ───────────────────────── Проклейка по заданиям ───────────────────────── */

/** Служебный ШК «Подтвердить» (наклейка на стол). */
export const SERVICE_CONFIRM_BARCODE = 'PKR-CONFIRM';

export function isServiceConfirmCode(code) {
  return String(code ?? '').trim().toUpperCase() === SERVICE_CONFIRM_BARCODE;
}

/** Единые тексты скана ТСД (сборка и проклейка). */
export const SCAN_UNRECOGNIZED_MESSAGE = 'Штрихкод не распознан';
export const NO_CURRENT_TARGET_MESSAGE = 'Нет текущего товара';
export const NO_PRINTER_MESSAGE = 'Выберите принтер в настройках печати';
export const STEP_LOCKED_MESSAGES = Object.freeze({
  confirm: 'Сначала подтвердите или отмените текущий товар',
  verify: 'Сначала отсканируйте напечатанную этикетку',
});

/** Шаг клиента на экране задания проклейки. */
export const LABELING_STEPS = Object.freeze({
  IDLE: 'idle',
  CONFIRM: 'confirm',
  VERIFY: 'verify',
  IN_HANDS: 'in_hands',
});

/** Состояния заказа (сервер) → подпись и цвет чипа. */
export const LABELING_ORDER_STATES = Object.freeze({
  blocked: { label: 'Ждёт МС', color: 'warning' },
  transferred: { label: 'Передан', color: 'default' },
  awaiting_verify: { label: 'Напечатан', color: 'info' },
  in_hands: { label: 'В руках', color: 'primary' },
  on_pallet: { label: 'На палете', color: 'success' },
  departed: { label: 'Выбыл', color: 'error' },
});

/** Режим проклейки задания: палеты после передачи (A) или печать/сверка при подборе (B). */
export const LABELING_MODES = Object.freeze({
  AFTER_TRANSFER: 'after_transfer',
  DURING_PICKING: 'during_picking',
});

export const LABELING_MODE_LABELS = Object.freeze({
  after_transfer: 'Палеты после сборки',
  during_picking: 'Проклейка при сборке',
});

export function normalizeLabelingMode(value) {
  return value === LABELING_MODES.DURING_PICKING ? LABELING_MODES.DURING_PICKING : LABELING_MODES.AFTER_TRANSFER;
}

/** job — DTO задания (подбор/проклейка) или сама строка режима. */
export function isDuringPickingMode(jobOrMode) {
  const mode = typeof jobOrMode === 'string' ? jobOrMode : jobOrMode?.labeling_mode;
  return normalizeLabelingMode(mode) === LABELING_MODES.DURING_PICKING;
}

export function labelingModeInfo(mode) {
  const value = normalizeLabelingMode(mode);
  return {
    value,
    label: LABELING_MODE_LABELS[value],
    color: value === LABELING_MODES.DURING_PICKING ? 'secondary' : 'default',
  };
}

const REMAINING_ORDER_STATES = new Set(['blocked', 'transferred', 'awaiting_verify', 'in_hands']);

export function labelingOrderStateInfo(state, mode = null) {
  if (state === 'in_hands' && isDuringPickingMode(mode)) return { label: 'Проклеен', color: 'primary' };
  return LABELING_ORDER_STATES[state] || { label: state || '—', color: 'default' };
}

/**
 * Чип позиции задания подбора: Ожидает / Подобран / Напечатан / Проклеен / Передан / На палете.
 * Стадии печати и сверки бывают только в режиме during_picking.
 */
export function pickingItemStageInfo(item, mode = null) {
  if (item?.status !== 'picked') return { code: 'pending', label: 'Ожидает', color: 'default' };
  if (item.transfer_id != null) {
    if (item.labeling_state === 'on_pallet') return { code: 'on_pallet', label: 'На палете', color: 'success' };
    return { code: 'transferred', label: 'Передан', color: 'secondary' };
  }
  if (isDuringPickingMode(mode)) {
    if (item.labeling_state === 'in_hands') return { code: 'labeled', label: 'Проклеен', color: 'success' };
    if (item.labeling_state === 'awaiting_verify') return { code: 'printed', label: 'Напечатан', color: 'info' };
  }
  return { code: 'picked', label: 'Подобран', color: 'primary' };
}

export function isRemainingLabelingOrder(order) {
  return REMAINING_ORDER_STATES.has(order?.labeling_state);
}

const ORDER_SORT_COLLATOR = new Intl.Collator('ru', { numeric: true, sensitivity: 'base' });

/** Заказы задания пачками по артикулу (затем по номеру), без мутации исходного массива. */
export function sortLabelingOrdersByArticle(orders) {
  const key = (order) => String(order?.article ?? '').trim();
  const id = (order) => String(order?.external_id ?? order?.order_key ?? '');
  return [...(orders || [])].sort((x, y) => {
    const ax = key(x);
    const ay = key(y);
    if (!ax !== !ay) return ax ? -1 : 1;
    return ORDER_SORT_COLLATOR.compare(ax, ay) || ORDER_SORT_COLLATOR.compare(id(x), id(y));
  });
}

/**
 * `/fulfillment/unified/labeling/jobs[/:jobId][/suffix]`.
 * suffix — 'take' | 'release' | 'scan' | 'flush' | 'pallets' | `orders/<key>/confirm` …
 */
export function labelingJobsPath(jobId = null, suffix = '') {
  let path = `${fulfillmentApiBase()}/labeling/jobs`;
  if (jobId != null && String(jobId) !== '') path += `/${encodeURIComponent(String(jobId))}`;
  const tail = String(suffix || '').replace(/^\/+/, '');
  return tail ? `${path}/${tail}` : path;
}

/** ТСД-ссылка на экран задания проклейки; job — LabelingJobDTO или my-session `{job_id, marketplace, account_id}`. */
export function labelingJobPath(job, fallbackAccountId = null) {
  return fulfillmentTsdPath(`/fulfillment/labeling/${job?.job_id ?? job?.id}`, {
    mp: job?.marketplace,
    accountId: job?.account_id ?? fallbackAccountId,
  });
}

/** `/labeling/jobs/:jobId/orders/:orderKey/:action` (confirm | reprint). */
export function labelingOrderPath(jobId, orderKey, action) {
  return labelingJobsPath(jobId, `orders/${encodeURIComponent(String(orderKey ?? ''))}/${action}`);
}

/** Этап 1 режима during_picking: `/labeling/jobs/:jobId/picking/<suffix>` (scan | orders/:key/confirm …). */
export function pickingLabelingPath(jobId, suffix = '') {
  const tail = String(suffix || '').replace(/^\/+/, '');
  return labelingJobsPath(jobId, tail ? `picking/${tail}` : 'picking');
}

/** Ключ подтверждения в режиме during_picking: заказ (повтор печати) или `product:<product_key>` (подбор единицы). */
export function pickingConfirmKey(current) {
  if (!current) return null;
  if (current.is_product) return current.product_key ? `product:${current.product_key}` : null;
  return current.order_key ?? null;
}

export function pickingLabelingOrderPath(jobId, key, action) {
  return pickingLabelingPath(jobId, `orders/${encodeURIComponent(String(key ?? ''))}/${action}`);
}

export function serviceLabelPdfPath(code = 'confirm') {
  return `${fulfillmentApiBase()}/labeling/service-labels/${encodeURIComponent(String(code))}.pdf`;
}

function toCount(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function textOrNull(value) {
  if (value == null) return null;
  const text = String(value).trim();
  return text ? text : null;
}

/**
 * Номер этикетки для крупного показа.
 * WB: `{a, b}` — part b печатается крупно; иначе одна строка.
 */
export function labelNumberDisplay(order) {
  if (!order) return { text: null, a: null, b: null };
  const partsRaw = order.label_number_parts;
  const a = textOrNull(partsRaw?.a);
  const b = textOrNull(partsRaw?.b);
  const display = textOrNull(order.label_number_display);
  if (a || b) {
    return { text: display || [a, b].filter(Boolean).join(' '), a, b };
  }
  return { text: display, a: null, b: null };
}

/** OrderLabelingDTO → безопасный объект для UI (все поля могут отсутствовать). */
export function normalizeLabelingOrder(dto, marketplace) {
  if (!dto || typeof dto !== 'object') return null;
  const mp = normalizeFulfillmentMp(dto.marketplace || marketplace) || 'wb';
  const externalId = textOrNull(dto.external_id)
    || textOrNull(dto.posting_number)
    || textOrNull(dto.yandex_order_id)
    || textOrNull(dto.wb_order_id)
    || textOrNull(dto.order_key);
  const orderKey = textOrNull(dto.order_key) || externalId;
  const cargoPlaces = Array.isArray(dto.cargo_places) ? dto.cargo_places : [];
  const labelsTotal = toCount(dto.labels_total) || cargoPlaces.length || 0;
  const label = labelNumberDisplay(dto);
  return {
    ...dto,
    marketplace: mp,
    order_key: orderKey,
    external_id: externalId,
    article: textOrNull(dto.article) || textOrNull(dto.offer_id),
    name: textOrNull(dto.name),
    image_url: dto.image_url || null,
    nm_id: dto.nm_id ?? null,
    barcode: textOrNull(dto.barcode),
    is_b2b: Boolean(dto.is_b2b),
    labeling_state: dto.labeling_state || 'transferred',
    label_number_display: label.text,
    label_number_parts: label.a || label.b ? { a: label.a, b: label.b } : null,
    label_error: dto.label_error || null,
    printed_at: dto.printed_at || null,
    verified_at: dto.verified_at || null,
    pallet: dto.pallet && dto.pallet.id != null ? dto.pallet : null,
    departed: dto.departed || null,
    composition_alert: Boolean(dto.composition_alert),
    cargo_places: cargoPlaces,
    cargo_place_current: dto.cargo_place_current || null,
    labels_verified: toCount(dto.labels_verified),
    labels_total: labelsTotal,
    transfer_id: dto.transfer_id ?? null,
    transfer_seq: dto.transfer_seq ?? null,
  };
}

/**
 * Карточка товара из ScanResultDTO kind 'product' (режим during_picking):
 * `is_product: true`, `order_key: null` — на шаге confirm ещё ничего не записано.
 */
export function normalizeScanProduct(dto, marketplace) {
  if (!dto || typeof dto !== 'object') return null;
  const productKey = textOrNull(dto.product_key);
  if (!productKey) return null;
  const mp = normalizeFulfillmentMp(dto.marketplace || marketplace) || 'wb';
  return {
    ...dto,
    is_product: true,
    marketplace: mp,
    product_key: productKey,
    order_key: null,
    article: textOrNull(dto.article) || textOrNull(dto.offer_id),
    name: textOrNull(dto.name),
    image_url: dto.image_url || null,
    barcode: textOrNull(dto.barcode),
    nm_id: dto.nm_id ?? null,
    sku: dto.sku ?? null,
    offer_id: textOrNull(dto.offer_id),
    is_b2b: Boolean(dto.is_b2b),
    total: toCount(dto.total),
    picked: toCount(dto.picked),
    pending: toCount(dto.pending),
    stock: dto.stock || null,
  };
}

/** Остаток МС запрещает подбор (как в диалоге пачки). */
export function scanProductBlocked(product) {
  const stock = product?.stock;
  if (!stock) return false;
  if (stock.can_pick === false) return true;
  return Boolean(stock.known) && !stock.stale && Number(stock.available) <= 0;
}

/** Yandex: «место k из N» для текущего (непроверенного) грузоместа. */
export function cargoPlaceProgress(order) {
  const total = toCount(order?.labels_total);
  if (total < 2) return null;
  const verified = Math.min(total, toCount(order?.labels_verified));
  const fromCurrent = toCount(order?.cargo_place_current?.index ?? order?.cargo_place_current?.place_index);
  const current = Math.min(total, fromCurrent || verified + 1);
  return { current, total, verified, done: verified >= total };
}

/** Yandex: «на палете k из N» — грузоместа многоместного заказа, уже привязанные к палетам. */
export function cargoPlacesOnPallet(order) {
  const total = toCount(order?.places_total) || toCount(order?.labels_total);
  if (total < 2 || order?.places_on_pallet == null) return null;
  const onPallet = Math.min(total, toCount(order.places_on_pallet));
  return { onPallet, total, done: onPallet >= total };
}

function normalizeProgress(progress = {}) {
  const p = progress || {};
  return {
    total: toCount(p.total),
    picked: toCount(p.picked),
    transferred: toCount(p.transferred),
    awaiting: toCount(p.awaiting),
    in_hands: toCount(p.in_hands),
    labeled: toCount(p.labeled),
    departed: toCount(p.departed),
    remaining: toCount(p.remaining),
  };
}

/** LabelingJobDTO / LabelingJobDetailDTO → объект для UI с дефолтами. */
export function normalizeLabelingJob(dto) {
  if (!dto || typeof dto !== 'object') return null;
  const marketplace = normalizeFulfillmentMp(dto.marketplace) || 'wb';
  const labeling = dto.labeling || {};
  const job = {
    ...dto,
    marketplace,
    progress: normalizeProgress(dto.progress),
    transfers: Array.isArray(dto.transfers) ? dto.transfers : [],
    labeling: {
      locked_by: labeling.locked_by ?? null,
      locked_by_name: labeling.locked_by_name || null,
      taken_at: labeling.taken_at || null,
      mine: Boolean(labeling.mine),
      can_take: labeling.can_take !== false,
      done_at: labeling.done_at || null,
      // Чужое взятое задание доступно супервайзеру только на просмотр (план 4a.1).
      read_only: labeling.read_only === true,
    },
    pallets_open: (Array.isArray(dto.pallets_open) ? dto.pallets_open : [])
      .filter((pallet) => pallet && pallet.id != null),
  };
  if (Array.isArray(dto.orders) || Array.isArray(dto.in_hands)) {
    job.orders = (dto.orders || []).map((row) => normalizeLabelingOrder(row, marketplace)).filter(Boolean);
    job.in_hands = (dto.in_hands || []).map((row) => normalizeLabelingOrder(row, marketplace)).filter(Boolean);
  }
  job.service_barcodes = { confirm: SERVICE_CONFIRM_BARCODE, ...(dto.service_barcodes || {}) };
  return job;
}

/** Сводка задания из ответа scan/flush/release поверх детальной (orders сохраняем). */
export function mergeLabelingJob(detail, summary) {
  const next = normalizeLabelingJob(summary);
  if (!next) return detail;
  if (!detail) return next;
  return {
    ...detail,
    ...next,
    orders: next.orders || detail.orders,
    in_hands: next.in_hands || detail.in_hands,
  };
}

/**
 * Ответ scan/confirm поверх детального задания без GET: сводка из `data.job`, заказ из `data.order`.
 * `refresh: true` — ответа мало для списка (палета, flush, незнакомый заказ), нужен полный GET.
 */
export function applyLabelingScanResult(detail, data) {
  if (!data) return { job: detail, refresh: false };
  const job = data.job ? mergeLabelingJob(detail, data.job) : detail;
  const flushed = Array.isArray(data.flushed) ? data.flushed : [];
  if (data.kind === 'pallet' || flushed.length) return { job, refresh: true };
  if (!data.order || !Array.isArray(job?.orders)) return { job, refresh: Boolean(data.order) };
  const order = normalizeLabelingOrder(data.order, job.marketplace);
  const key = String(order?.order_key ?? '');
  const index = job.orders.findIndex((row) => String(row.order_key) === key);
  if (!order || index < 0) return { job, refresh: true };
  const orders = [...job.orders];
  orders[index] = { ...orders[index], ...order };
  const inHands = (job.in_hands || []).filter((row) => String(row.order_key) !== key);
  if (order.labeling_state === 'in_hands') inHands.push(orders[index]);
  return { job: { ...job, orders, in_hands: inHands }, refresh: false };
}

/** Строка остатка МС для ТСД; кэш скана может быть «уточняется» или устаревшим. */
export function pickingStockText(stock) {
  if (!stock) return null;
  if (!stock.known) return stock.message || 'Остаток МС недоступен';
  const text = `МС ${stock.ms_stock ?? 0} · резерв ${stock.reserved_total ?? 0} · доступно ${stock.available ?? 0}`;
  return stock.stale ? `${text} · уточняется` : text;
}

/** Партии с ошибкой перемещения МС. */
export function transferMoveErrors(transfers = []) {
  return (Array.isArray(transfers) ? transfers : [])
    .filter((transfer) => transfer?.ms_move_status === 'error');
}

/** Партия передачи → короткая подпись статуса МС. */
export function transferMoveInfo(transfer) {
  const status = transfer?.ms_move_status || null;
  if (!transfer?.ms_move_required && !status) return { mark: '', label: 'МС не нужно', color: 'default' };
  if (status === 'done') return { mark: '✓', label: 'МС ✓', color: 'success' };
  if (status === 'error') return { mark: '✗', label: 'МС ✗', color: 'error' };
  if (status === 'in_progress') return { mark: '⏳', label: 'МС ⏳', color: 'warning' };
  return { mark: '…', label: 'МС —', color: 'default' };
}

export function transferChipLabel(transfer) {
  const seq = transfer?.seq_no != null ? `№${transfer.seq_no}` : 'Партия';
  const count = toCount(transfer?.items_count);
  const info = transferMoveInfo(transfer);
  return [seq, `${count} шт`, info.mark ? info.label : null].filter(Boolean).join(' · ');
}

/** Русские сообщения для кодов ошибок проклейки; серверный `message` приоритетнее. */
export const LABELING_ERROR_MESSAGES = Object.freeze({
  label_not_printed: 'Этикетка ещё не напечатана — отсканируй товар и подтверди печать',
  already_labeled: 'Заказ уже на палете',
  no_current_order: 'Сначала отсканируй товар',
  pallet_foreign_job: 'Палета не из этого задания',
  labeling_locked: 'Задание уже взял другой сотрудник',
  labeling_not_owner: 'Сначала возьми задание в работу',
  labeling_user_busy: 'У вас уже есть задание в работе',
  labeling_not_ready: 'Задание ещё не готово к проклейке',
  item_transferred: 'Позиция уже передана на проклейку',
  orders_left_mp: 'Заказ уже выбыл на маркетплейсе',
  labeling_mode_mismatch: 'Действие недоступно в режиме проклейки этого задания',
  label_printed: 'Этикетка уже напечатана — отменить подбор нельзя',
  job_closed: 'Задание закрыто',
  product_all_picked: 'Этот товар уже собран полностью',
  ms_out_of_stock: 'Недостаточно остатка МойСклад — подбор заблокирован',
  confirmation_mismatch: 'Подтверждение не совпало с товаром — отсканируй товар ещё раз',
  step_locked: STEP_LOCKED_MESSAGES.confirm,
  labels_unverified: 'Сначала проверьте этикетки всех подобранных заказов',
  barcode_unrecognized: SCAN_UNRECOGNIZED_MESSAGE,
  scan_unrecognized: SCAN_UNRECOGNIZED_MESSAGE,
});

/** Ошибка с `details.composition_alert` (Ozon/Yandex): печать разрешена только кнопкой. */
export function isCompositionAlertError(data) {
  return data?.code === 'validation_error' && Boolean(data?.details?.composition_alert);
}

/**
 * Текст ошибки проклейки по телу ответа `{code, message, details}`:
 * серверное сообщение, иначе русский дефолт по коду; `already_labeled` дополняется кодом палеты.
 */
export function labelingErrorMessage(data) {
  if (!data || typeof data !== 'object') return '';
  const code = data.code || null;
  const base = textOrNull(data.message) || LABELING_ERROR_MESSAGES[code] || '';
  const details = data.details || {};
  if (code === 'already_labeled' && details.pallet) {
    const palletCode = details.pallet.code || (details.pallet.id != null ? `#${details.pallet.id}` : '');
    if (palletCode && !base.includes(palletCode)) return `${base} (палета ${palletCode})`;
  }
  if (isCompositionAlertError(data) && !base) {
    return 'Состав изменился — подтвердите печать кнопкой';
  }
  return base;
}

/** 409 product_all_picked → «Уже собрано: ART · 5 из 5». */
export function allPickedNotice(data) {
  const details = data?.details || {};
  const article = textOrNull(details.product?.article) || textOrNull(details.article);
  const total = toCount(details.total);
  const parts = [article, total ? `${total} из ${total}` : null].filter(Boolean);
  return `Уже собрано: ${parts.length ? parts.join(' · ') : 'товар собран полностью'}`;
}

const SCAN_RESET_CODES = new Set(['no_current_order', 'confirmation_mismatch']);
const UNRECOGNIZED_CODES = new Set(['barcode_unrecognized', 'scan_unrecognized']);

/**
 * Ошибка скана/подтверждения ТСД → `{ severity, message, resetStep }`.
 * «Уже готово» — info, неизвестный ШК — warning, не по шагу (step_locked) — error; шаг сбрасывают
 * только коды, после которых текущего товара/заказа на сервере нет.
 */
export function scanErrorFeedback(data) {
  const code = data?.code || null;
  if (code === 'step_locked') {
    const step = data?.details?.step;
    return {
      severity: 'error',
      message: textOrNull(data.message) || STEP_LOCKED_MESSAGES[step] || STEP_LOCKED_MESSAGES.confirm,
      resetStep: false,
    };
  }
  if (code === 'product_all_picked') return { severity: 'info', message: allPickedNotice(data), resetStep: false };
  if (code === 'already_labeled') return { severity: 'info', message: labelingErrorMessage(data), resetStep: false };
  if (UNRECOGNIZED_CODES.has(code)) return { severity: 'warning', message: SCAN_UNRECOGNIZED_MESSAGE, resetStep: false };
  return { severity: 'error', message: labelingErrorMessage(data), resetStep: SCAN_RESET_CODES.has(code) };
}

/** Поля шага ТСД в теле scan: `current_step` + ключ текущего заказа/товара. */
export function scanStepBody(state) {
  const step = state?.step;
  const current = state?.current || null;
  const active = current && step && step !== LABELING_STEPS.IDLE;
  return {
    current_step: active ? step : null,
    current_order_key: active && !current.is_product ? current.order_key || undefined : undefined,
    current_product_key: active ? current.product_key || undefined : undefined,
  };
}

/** PKR-CONFIRM печатает (confirm) или повторяет печать (verify) — без принтера не отправляем. */
export function serviceScanNeedsPrinter(step) {
  return step === LABELING_STEPS.CONFIRM || step === LABELING_STEPS.VERIFY;
}

/** Номер заказа позиции подбора (WB wb_order_id / Ozon posting_number / Yandex yandex_order_id). */
export function pickingItemOrderKey(item) {
  const task = item?.task || {};
  return textOrNull(task.wb_order_id) || textOrNull(task.posting_number) || textOrNull(task.yandex_order_id);
}

const UNVERIFIED_LIST_LIMIT = 5;

/**
 * Режим during_picking: на склад отгрузки уходят только готовые позиции — подобранные с проверенной
 * этикеткой (`in_hands`; у Yandex — после всех мест). Подобранные без печати и `awaiting_verify` — непроверенные.
 * `job.labeling_progress.unverified` (если бэк прислал) приоритетнее подсчёта по позициям.
 * → `{ ready, unverified, unverifiedOrders }`; номера заказов — только если непроверенных ≤ 5.
 */
export function pickingLabelingCounts(job, items = null) {
  const list = Array.isArray(items) ? items : (Array.isArray(job?.items) ? job.items : []);
  const open = list.filter((item) => item?.status === 'picked' && item.transfer_id == null);
  const readyItems = open.filter((item) => item.labeling_state === 'in_hands');
  const unverifiedItems = open.filter((item) => item.labeling_state !== 'in_hands');
  const progress = job?.labeling_progress && typeof job.labeling_progress === 'object' ? job.labeling_progress : null;
  const unverified = progress?.unverified != null ? toCount(progress.unverified) : unverifiedItems.length;
  const ready = list.length || !progress
    ? readyItems.length
    : Math.max(0, toCount(progress.verified) - toCount(progress.transferred));
  const orders = unverifiedItems.map(pickingItemOrderKey).filter(Boolean);
  return {
    ready,
    unverified,
    unverifiedOrders: unverified > 0 && unverified <= UNVERIFIED_LIST_LIMIT ? orders.slice(0, UNVERIFIED_LIST_LIMIT) : [],
  };
}

/**
 * Сборка в режиме during_picking: первый напечатанный, но не проверенный заказ (до передачи) →
 * карточка шага verify. Номер этикетки и грузоместа (Yandex) — из полей позиции JobDTO
 * (`label_number_display/parts`, `labels_total/verified`, `cargo_place_current`).
 */
export function resumePickingVerify(items, marketplace, { excludeKeys = [] } = {}) {
  const exclude = new Set((excludeKeys || []).filter((key) => key != null).map(String));
  const item = (Array.isArray(items) ? items : []).find((row) => row?.status === 'picked'
    && row.transfer_id == null && row.labeling_state === 'awaiting_verify'
    && !exclude.has(String(pickingItemOrderKey(row) ?? '')));
  if (!item) return null;
  const task = item.task || {};
  const lines = Array.isArray(item.lines) ? item.lines : [];
  const orderKey = pickingItemOrderKey(item);
  if (!orderKey) return null;
  return normalizeLabelingOrder({
    order_key: orderKey,
    external_id: orderKey,
    article: item.product?.article || task.offer_id || task.article || lines[0]?.offer_id || lines[0]?.article,
    name: item.product?.name || task.name || lines[0]?.name,
    image_url: item.product?.image_url || task.image_url || lines[0]?.image_url,
    barcode: item.product?.barcode || task.barcode || lines[0]?.barcode,
    nm_id: task.nm_id ?? null,
    sku: task.sku ?? lines[0]?.sku ?? null,
    is_b2b: task.is_b2b,
    labeling_state: 'awaiting_verify',
    label_number_display: item.label_number_display ?? null,
    label_number_parts: item.label_number_parts ?? null,
    labels_total: item.labels_total ?? null,
    labels_verified: item.labels_verified ?? null,
    cargo_place_current: item.cargo_place_current ?? null,
  }, marketplace);
}

/**
 * Шаг при входе в задание проклейки: сценарий A — первый «в руках»; иначе первый напечатанный,
 * но не проверенный заказ (verify).
 */
export function resumeLabelingState(job) {
  if (!job) return { ...IDLE_STATE };
  if (job.in_hands?.length && !isDuringPickingMode(job)) {
    return { current: job.in_hands[0], step: LABELING_STEPS.IN_HANDS };
  }
  return nextAwaitingVerifyState(job.orders);
}

/** Первый (по списку, по артикулу) напечатанный непроверенный заказ проклейки → шаг verify, иначе idle. */
export function nextAwaitingVerifyState(orders, { excludeKeys = [] } = {}) {
  const exclude = new Set((excludeKeys || []).filter((key) => key != null).map(String));
  const awaiting = sortLabelingOrdersByArticle(orders || [])
    .find((order) => order.labeling_state === 'awaiting_verify' && !exclude.has(String(order.order_key)));
  return awaiting ? { current: awaiting, step: LABELING_STEPS.VERIFY } : { ...IDLE_STATE };
}

/** FlushedDTO[] → текст тоста «123 → ПЛ-5». */
export function flushedNotice(flushed) {
  const rows = (Array.isArray(flushed) ? flushed : []).filter(Boolean);
  if (!rows.length) return '';
  const byPallet = new Map();
  for (const row of rows) {
    const code = row.pallet?.code || (row.pallet?.id != null ? `#${row.pallet.id}` : '—');
    if (!byPallet.has(code)) byPallet.set(code, []);
    byPallet.get(code).push(row.external_id || row.order_key || '—');
  }
  return [...byPallet.entries()]
    .map(([code, ids]) => `${ids.join(', ')} → на палету ${code}`)
    .join('\n');
}

export function flushedIncludes(flushed, orderKey) {
  if (orderKey == null) return false;
  return (Array.isArray(flushed) ? flushed : [])
    .some((row) => String(row?.order_key ?? '') === String(orderKey));
}

/**
 * Подсказка под полем скана.
 * context: 'labeling' (склад отгрузки) | 'picking' (сборка в режиме during_picking).
 */
export function labelingStepHint(step, current, { mode = null, context = 'labeling' } = {}) {
  if (step === LABELING_STEPS.CONFIRM) {
    return current?.is_product
      ? 'Сканируй ШК «Подтвердить» или нажми «Подтвердить и напечатать»'
      : 'Сканируй ШК «Подтвердить» или нажми «Печать этикетки»';
  }
  if (step === LABELING_STEPS.VERIFY) {
    const place = cargoPlaceProgress(current);
    if (place && !place.done) return `Сканируй этикетку грузоместа ${place.current} из ${place.total}`;
    const onPallet = context === 'labeling' && isDuringPickingMode(mode) ? cargoPlacesOnPallet(current) : null;
    if (onPallet && !onPallet.done) {
      return `Сканируй этикетку следующего грузоместа — на палете ${onPallet.onPallet} из ${onPallet.total}`;
    }
    return 'Сканируй напечатанную этикетку';
  }
  if (step === LABELING_STEPS.IN_HANDS) return 'Сканируй следующий товар — предыдущий ляжет на палету';
  if (context === 'labeling' && isDuringPickingMode(mode)) return 'Сканируй этикетку заказа — он ляжет на палету';
  return 'Сканируй товар';
}

const IDLE_STATE = Object.freeze({ current: null, step: LABELING_STEPS.IDLE });

/**
 * Переходы экрана задания по `kind` ответа scan/confirm.
 * Каждый обработчик: (result, { current, step }, { mode }) → { current, step }.
 * В режиме during_picking шага in_hands нет: проверенная этикетка → idle.
 */
export const scanKindHandlers = Object.freeze({
  order: (result, state) => {
    const order = normalizeLabelingOrder(result?.order) || state.current;
    const step = result?.next_step === 'verify' ? LABELING_STEPS.VERIFY : LABELING_STEPS.CONFIRM;
    return { current: order, step: order ? step : LABELING_STEPS.IDLE };
  },
  product: (result) => {
    const product = normalizeScanProduct(result?.product);
    return product ? { current: product, step: LABELING_STEPS.CONFIRM } : { ...IDLE_STATE };
  },
  confirm: (result, state) => {
    // Yandex: заказ ещё не собран целиком — печати нет, ждём следующий товар.
    if (result?.next_step === 'next_product') return { ...IDLE_STATE };
    const order = normalizeLabelingOrder(result?.order) || state.current;
    // Сценарий A, шаг in_hands: PKR-CONFIRM = «На палету».
    if (order?.labeling_state === 'on_pallet' || flushedIncludes(result?.flushed, state.current?.order_key)) {
      return { ...IDLE_STATE };
    }
    return { current: order, step: LABELING_STEPS.VERIFY };
  },
  /** «Уже собрано / уже на палете / нет текущего товара» — шаг не меняется. */
  notice: (_result, state) => state,
  label: (result, state, ctx = {}) => {
    const order = normalizeLabelingOrder(result?.order) || state.current;
    if (result?.next_step === 'verify') return { current: order, step: LABELING_STEPS.VERIFY };
    if (!order || isDuringPickingMode(ctx.mode)) return { ...IDLE_STATE };
    if (order.labeling_state === 'on_pallet' || flushedIncludes(result?.flushed, order.order_key)) {
      return { ...IDLE_STATE };
    }
    return { current: order, step: LABELING_STEPS.IN_HANDS };
  },
  pallet: (_result, state) => state,
  unrecognized: (result, state) => {
    const gone = state.step === LABELING_STEPS.IN_HANDS
      || flushedIncludes(result?.flushed, state.current?.order_key);
    return gone ? { current: null, step: LABELING_STEPS.IDLE } : state;
  },
});

export function nextLabelingState(result, state, ctx = {}) {
  const handler = scanKindHandlers[result?.kind];
  const base = { current: state?.current || null, step: state?.step || LABELING_STEPS.IDLE };
  if (!handler) return base;
  const next = handler(result, base, ctx);
  // Текущий заказ уехал на палету тем же действием (например, flush перед новым сканом).
  if (next.current && result?.kind !== 'order' && result?.kind !== 'label'
    && flushedIncludes(result?.flushed, next.current.order_key)) {
    return { current: null, step: LABELING_STEPS.IDLE };
  }
  return next;
}

/** Сколько собранных позиций ещё не передано на проклейку. */
export function pickingTransferableCount(job, items = null) {
  const list = Array.isArray(items) ? items : (Array.isArray(job?.items) ? job.items : null);
  if (list) {
    return list.filter((item) => item?.status === 'picked' && item?.transfer_id == null).length;
  }
  const picked = toCount(job?.items_picked);
  const transferred = toCount(job?.items_transferred);
  return Math.max(0, picked - transferred);
}

/**
 * Подбор на ТСД всегда двухшаговый (scan/resolve → диалог с пачечным счётчиком → picks/confirm)
 * для WB, Ozon и Yandex; одношаговый `POST /picking-jobs/:id/scan` остался только для старых ТСД.
 */
export function pickingUsesTwoStepConfirm(_marketplace) {
  return true;
}

function withTransferFields(item) {
  return {
    ...item,
    transfer_id: item.transfer_id ?? null,
    transferred_at: item.transferred_at || null,
    labeling_state: item.labeling_state || null,
  };
}

/**
 * Normalize picking-job item for shared TSD list UI.
 * Ozon items expose `posting`; WB items expose `task`.
 */
export function normalizePickingJobItem(item, marketplace) {
  if (!item) return item;
  if (!isLocalWarehouseMp(marketplace)) return withTransferFields(item);
  const posting = item.posting || item.order || {};
  const task = marketplaceOrderAsTask({
    ...posting,
    account_id: item.account_id || posting.account_id,
    yandex_order_id: posting.yandex_order_id || item.yandex_order_id,
    posting_number: posting.posting_number || item.posting_number,
  }, marketplace);
  return withTransferFields({
    ...item,
    task,
    product: {
      article: item.product?.article || task.article,
      image_url: item.product?.image_url || task.image_url,
      barcode: item.product?.barcode || task.barcode,
    },
  });
}

export function yandexShipmentClosePath(shipmentId) {
  return `/fulfillment/yandex/shipments/${encodeURIComponent(String(shipmentId))}/close`;
}

export function yandexShipmentBarcodePrintPath(shipmentId) {
  return `/fulfillment/yandex/shipments/${encodeURIComponent(String(shipmentId))}/barcode-print`;
}

export function yandexShipmentActPath(shipmentId) {
  return `/fulfillment/yandex/shipments/${encodeURIComponent(String(shipmentId))}/act`;
}

/** Whether loading session needs account_id (Ozon) and supports vehicle label (WB). */
export function loadingSessionCapabilities(marketplace) {
  const mp = normalizeFulfillmentMp(marketplace);
  const isOzon = mp === 'ozon';
  const isWb = mp === 'wb';
  return {
    requiresAccount: isOzon || mp === 'yandex',
    supportsVehicleLabel: isWb,
    supportsFinalSuppliesList: isWb,
  };
}
